import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ScanLine, Hotel as HotelIcon, Package, Clock, AlertTriangle, History, StickyNote, Pencil } from "lucide-react";
import PdaLayout from "./PdaLayout";
import { beep, primeAudio } from "@/lib/pdaSounds";
import { usePdaScan } from "@/hooks/usePdaScan";
import usePdaScenarioHint from "@/hooks/usePdaScenarioHint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Identify — scan one RFID and see everything we know about it:
 * product, current location (warehouse vs which hotel), missing flag,
 * usage stats, and the most recent few history events. Useful when the
 * operator finds a stray tag and wants to know "what is this and where
 * does it belong?".
 *
 * Pressing the middle key while on this page fires the same RFID scan
 * event as elsewhere; we only ever process the LAST tag of the burst.
 */
export default function PdaIdentify({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);

  usePdaScenarioHint("Identify"); // dedicated low-power profile for single-tag reads
  useEffect(() => { primeAudio(); }, []);

  const lookup = useCallback(async (rfid) => {
    if (!rfid) return;
    setLoading(true);
    setError(null);
    setItem(null);
    try {
      const res = await axios.get(`${API}/linen/${encodeURIComponent(rfid)}`);
      setItem(res.data);
      beep.ok();
    } catch (e) {
      if (e?.response?.status === 404) {
        setError({ rfid, code: "unknown" });
        beep.error();
      } else {
        toast.error("Lookup failed");
        beep.error();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto re-scan: every trigger pull looks up the freshest tag — no need to
  // tap "Scan another". We skip lookups while one is in flight and avoid
  // re-fetching the exact tag already on screen (stops flicker while the
  // trigger is held on the same item).
  const currentRfidRef = useRef(null);
  const loadingRef = useRef(false);

  const onEpcs = useCallback((epcs) => {
    if (!epcs || epcs.length === 0) return;
    if (loadingRef.current) return;
    // Use the LAST tag of the burst — usually the strongest read.
    const rfid = epcs[epcs.length - 1];
    if (rfid === currentRfidRef.current) return;
    currentRfidRef.current = rfid;
    loadingRef.current = true;
    lookup(rfid).finally(() => { loadingRef.current = false; });
  }, [lookup]);
  usePdaScan(onEpcs, { enabled: true });

  return (
    <PdaLayout
      title="Identify"
      subtitle="Scan a single tag"
      onBack={() => navigate("/pda/menu")}
      onLogout={onLogout}
    >
      {/* Idle / loading state */}
      {!item && !error && (
        <div className="text-center text-neutral-500 py-10">
          {loading ? (
            <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          ) : (
            <>
              <ScanLine className="h-12 w-12 mx-auto opacity-40 mb-2" />
              <div className="text-sm">Press the middle key to scan one tag</div>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4" data-testid="pda-identify-unknown">
          <div className="flex items-center gap-2 text-amber-900 font-bold">
            <AlertTriangle className="h-5 w-5" /> Unknown tag
          </div>
          <div className="mt-2 text-sm text-amber-900">This RFID isn't registered in the system.</div>
          <div className="mt-1 font-mono text-[11px] break-all bg-amber-100/60 rounded px-2 py-1">{error.rfid}</div>
          <button
            onClick={() => navigate("/pda/register")}
            data-testid="pda-identify-go-register"
            className="mt-3 w-full h-10 rounded-md bg-rose-600 text-white font-bold text-sm"
          >
            Register this tag →
          </button>
          <div className="mt-2 text-center text-xs text-neutral-500">Pull the trigger to scan the next tag</div>
        </div>
      )}

      {item && (
        <div className="space-y-3" data-testid="pda-identify-result">
          <div className="rounded-md border bg-white px-3 py-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-neutral-500">
              <Package className="h-3 w-3" /> Product
            </div>
            <div className="font-display font-bold text-2xl mt-1">{item.product_name || "—"}</div>
            <div className="mt-2 font-mono text-[10px] break-all text-neutral-500">{item.rfid}</div>
          </div>

          <div className="rounded-md border bg-white px-3 py-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-neutral-500">
              <HotelIcon className="h-3 w-3" /> Current location
            </div>
            <div className="mt-1 flex items-center gap-2">
              <StatusBadge status={item.status} />
              {item.current_hotel_name ? (
                <span className="text-sm font-medium">{item.current_hotel_name}</span>
              ) : (
                <span className="text-sm text-neutral-500">{item.status === "at_warehouse" ? "Warehouse" : "—"}</span>
              )}
              {item.is_missing ? (
                <span className="text-[9px] font-bold uppercase tracking-wider text-rose-700 ml-auto">missing</span>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Tile label="Usage" value={item.usage_count ?? 0} />
            <Tile label="Last dispatched" value={item.last_dispatched_at ? short(item.last_dispatched_at) : "—"} />
          </div>

          <NoteCard
            key={item.rfid}
            rfid={item.rfid}
            initialNotes={item.notes}
            onSaved={(notes) => setItem((prev) => (prev ? { ...prev, notes } : prev))}
          />

          {/* Most recent delivery — pulled from history so the operator sees
              at a glance WHICH hotel this item last went to. */}
          {(() => {
            const lastDelivery = (item.history || []).find(
              (h) => (h.event === "delivered" || h.event === "dispatched") && h.hotel_name
            );
            return lastDelivery ? (
              <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2" data-testid="pda-identify-last-delivery">
                <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold">Last delivered to</div>
                <div className="text-sm font-medium text-sky-900 mt-0.5">
                  {lastDelivery.hotel_name}
                  {lastDelivery.order_number ? <span className="text-sky-700"> · {lastDelivery.order_number}</span> : null}
                </div>
                {lastDelivery.timestamp && <div className="text-[11px] text-sky-700">{short(lastDelivery.timestamp)}</div>}
              </div>
            ) : null;
          })()}

          {item.is_overdue ? (
            <div className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800 flex items-center gap-2">
              <Clock className="h-4 w-4" /> Overdue at hotel
            </div>
          ) : null}

          {item.history && item.history.length > 0 && (
            <div className="rounded-md border bg-white" data-testid="pda-identify-history">
              <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-neutral-500 flex items-center gap-1.5">
                <History className="h-3 w-3" /> Recent history ({item.history.length})
              </div>
              <div className="divide-y">
                {item.history.slice(0, 10).map((h, i) => (
                  <HistoryRow key={i} h={h} />
                ))}
              </div>
            </div>
          )}

          <div className="text-center text-xs text-neutral-500 py-1">Pull the trigger to scan the next tag</div>
        </div>
      )}
    </PdaLayout>
  );
}

function StatusBadge({ status }) {
  const map = {
    at_warehouse: { label: "At warehouse", cls: "bg-emerald-100 text-emerald-800" },
    at_hotel:     { label: "At hotel",     cls: "bg-amber-100 text-amber-800" },
    in_wash:      { label: "In wash",      cls: "bg-sky-100 text-sky-800" },
    retired:      { label: "Retired",      cls: "bg-neutral-200 text-neutral-700" },
  };
  const m = map[status] || { label: status || "—", cls: "bg-neutral-100 text-neutral-700" };
  return <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${m.cls}`}>{m.label}</span>;
}

function Tile({ label, value }) {
  return (
    <div className="rounded-md border bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="font-display font-bold text-base mt-0.5">{value}</div>
    </div>
  );
}

// Note viewer/editor — keyed by rfid so state resets on every new scan.
function NoteCard({ rfid, initialNotes, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialNotes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/linen/${encodeURIComponent(rfid)}`, { notes: draft });
      onSaved(res.data.notes);
      setEditing(false);
      toast.success("Note saved");
    } catch {
      toast.error("Couldn't save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border bg-white px-3 py-2" data-testid="pda-identify-note">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-neutral-500">
          <StickyNote className="h-3 w-3" /> Note
        </div>
        {!editing && (
          <button
            onClick={() => { setDraft(initialNotes || ""); setEditing(true); }}
            data-testid="pda-identify-note-edit"
            className="text-[11px] text-sky-700 underline flex items-center gap-1"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-1.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            autoFocus
            data-testid="pda-identify-note-input"
            placeholder="Supplier, material, condition, complaint…"
            className="w-full text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-sky-400"
          />
          <div className="flex gap-2 mt-1.5">
            <button
              onClick={save}
              disabled={saving}
              data-testid="pda-identify-note-save"
              className="flex-1 h-9 rounded-md bg-sky-600 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              data-testid="pda-identify-note-cancel"
              className="flex-1 h-9 rounded-md border text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className={`mt-1 text-sm ${initialNotes ? "text-neutral-800" : "text-neutral-400 italic"}`} data-testid="pda-identify-note-text">
          {initialNotes || "No note"}
        </div>
      )}
    </div>
  );
}

function short(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Human-readable label + detail line for a linen-item history event. The
// backend uses two overlapping shapes across workflows: dispatch/receive/
// rewash use `event` + `timestamp` + `hotel_name` + `order_number`, while
// stocktake uses `type` + `at` + `details`. Fall through both.
const EVENT_LABELS = {
  delivered: "Delivered",
  dispatched: "Dispatched",
  received: "Received back",
  rewash_in: "Rewash (in)",
  re_registered: "Re-registered",
  stocktake_scan: "Stocktake — scanned",
  stocktake_recovered: "Stocktake — recovered",
  stocktake_missing: "Stocktake — flagged missing",
  register: "Registered",
};

function HistoryRow({ h }) {
  const kind = h.event || h.type || "event";
  const label = EVENT_LABELS[kind] || kind;
  const ts = h.timestamp || h.at;

  // Build a details line specific to the event kind. The stocktake shape
  // already has a pre-formatted `details` string so use it as-is.
  let detail = h.details || h.note || "";
  if (!detail) {
    if (kind === "delivered") {
      const parts = [];
      if (h.hotel_name) parts.push(`→ ${h.hotel_name}`);
      if (h.from_hotel_name && h.from_hotel_name !== h.hotel_name) {
        parts.push(`(from ${h.from_hotel_name})`);
      }
      if (h.order_number) parts.push(h.order_number);
      if (h.transfer) parts.push("· transfer");
      detail = parts.join(" ");
    } else if (kind === "dispatched") {
      const parts = [];
      if (h.hotel_name) parts.push(`→ ${h.hotel_name}`);
      if (h.order_number) parts.push(h.order_number);
      detail = parts.join(" · ");
    } else if (kind === "received" || kind === "rewash_in") {
      if (h.from_hotel_name) detail = `from ${h.from_hotel_name}`;
    }
  }

  return (
    <div className="px-3 py-2 text-xs" data-testid={`pda-history-row-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-neutral-800">{label}</span>
        {ts && <span className="text-neutral-400 text-[10px] tabular-nums shrink-0">{short(ts)}</span>}
      </div>
      {detail && <div className="text-neutral-600 text-[11px] mt-0.5 leading-snug">{detail}</div>}
    </div>
  );
}
